import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box, TextField, Button, Typography, Alert,
  CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/Person';
import LockOutlinedIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../services/api';
import FundoTecnologico from '../components/FundoTecnologico';
import CartaoAutenticacao from '../components/CartaoAutenticacao';
import { VERSION } from '../version';

const estiloBotaoPrincipal = {
  position: 'relative', overflow: 'hidden',
  background: 'linear-gradient(135deg, #13375F 0%, #e94560 130%)',
  py: 1.4, fontWeight: 700, fontSize: 15,
  boxShadow: '0 8px 24px rgba(233,69,96,0.35)',
  '&:hover': { background: 'linear-gradient(135deg, #0d2642 0%, #c73550 130%)' },
  '&::after': {
    content: '""', position: 'absolute', top: 0, left: '-60%',
    width: '40%', height: '100%',
    background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.35), transparent)',
    transform: 'skewX(-20deg)',
    animation: 'shimmer 3.2s ease-in-out infinite',
  },
};

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [modo, setModo] = useState('login');
  const [identificador, setIdentificador] = useState('');
  const [enviandoReset, setEnviandoReset] = useState(false);
  const [erroReset, setErroReset] = useState('');

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await login(username, password);
      navigate('/');
    } catch {
      setError('Usuário ou senha incorretos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const aoPressionarTecla = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  const handleAbrirEsqueci = () => {
    setModo('esqueci');
    setIdentificador(username);
    setErroReset('');
  };

  const handleEnviarReset = async () => {
    setErroReset('');
    if (!identificador.trim()) {
      setErroReset('Informe seu usuário ou e-mail.');
      return;
    }
    setEnviandoReset(true);
    try {
      await api.post('/auth/esqueci-senha/', { identificador });
      setModo('enviado');
    } catch {
      setErroReset('Erro ao solicitar redefinição. Tente novamente em instantes.');
    } finally {
      setEnviandoReset(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' } }}>
      <style>{`
        @keyframes derivaA { 0%,100% { transform: translate(0,0); } 50% { transform: translate(40px,30px); } }
        @keyframes derivaB { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-35px,-25px); } }
        @keyframes pisoMove { from { background-position: 0 0, 0 0; } to { background-position: 0 54px, 0 0; } }
        @keyframes focoGlow {
          0% { box-shadow: 0 0 0 0 rgba(233,69,96,0.4); }
          70% { box-shadow: 0 0 0 6px rgba(233,69,96,0); }
          100% { box-shadow: 0 0 0 0 rgba(233,69,96,0); }
        }
        @keyframes shimmer {
          0% { left: -60%; }
          100% { left: 130%; }
        }
        @media (prefers-reduced-motion: reduce) {
          [class*="deriva"], [class*="piso"] { animation: none !important; }
        }
      `}</style>

      <FundoTecnologico />

      <Box sx={{ position: 'absolute', left: { xs: '50%', md: 88 }, top: { xs: 56, md: '50%' }, transform: { xs: 'translateX(-50%)', md: 'translateY(-50%)' }, textAlign: { xs: 'center', md: 'left' }, zIndex: 1 }}>
        <Typography sx={{
          color: 'white', fontWeight: 800, letterSpacing: '0.01em', lineHeight: 0.95,
          fontSize: { xs: 42, md: 76 },
          background: 'linear-gradient(120deg, #ffffff 30%, #cfd8e3 60%, #ffffff 90%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          LEAN<br />WAY
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: { xs: 12, md: 15 }, letterSpacing: '0.35em', mt: 1 }}>
          CONSULTING
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.28)', fontSize: 11.5, fontFamily: 'monospace', mt: { xs: 3, md: 6 } }}>
          Sistema de Gestão · v{VERSION}
        </Typography>
      </Box>

      <CartaoAutenticacao>
        {modo === 'login' && (
          <>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#0d1b2e', mb: 0.5 }}>
              Bem-vindo(a) de volta
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
              Entre com suas credenciais para acessar o sistema
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

            <TextField
              fullWidth
              label="Usuário ou e-mail"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={aoPressionarTecla}
              autoFocus
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root.Mui-focused': { animation: 'focoGlow 1s ease-out' },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              fullWidth
              label="Senha"
              type={mostrarSenha ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={aoPressionarTecla}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root.Mui-focused': { animation: 'focoGlow 1s ease-out' },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setMostrarSenha(!mostrarSenha)} edge="end" size="small">
                        {mostrarSenha ? <VisibilityOffIcon sx={{ fontSize: 19 }} /> : <VisibilityIcon sx={{ fontSize: 19 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Box sx={{ textAlign: 'right', mb: 2.5 }}>
              <Typography
                onClick={handleAbrirEsqueci}
                sx={{ fontSize: 12.5, color: '#13375F', fontWeight: 600, cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
              >
                Esqueci minha senha
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLogin}
              disabled={loading}
              sx={estiloBotaoPrincipal}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Entrar'}
            </Button>
          </>
        )}

        {modo === 'esqueci' && (
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setModo('login')}
              sx={{ color: '#64748b', mb: 1, ml: -1 }}
            >
              Voltar
            </Button>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#0d1b2e', mb: 0.5 }}>
              Esqueceu sua senha?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
              Informe seu usuário ou e-mail cadastrado e enviaremos um link para você criar uma nova senha.
            </Typography>

            {erroReset && <Alert severity="error" sx={{ mb: 2.5 }}>{erroReset}</Alert>}

            <TextField
              fullWidth
              label="Usuário ou e-mail"
              value={identificador}
              onChange={(e) => setIdentificador(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleEnviarReset()}
              autoFocus
              sx={{ mb: 3 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleEnviarReset}
              disabled={enviandoReset}
              sx={estiloBotaoPrincipal}
            >
              {enviandoReset ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Enviar link de redefinição'}
            </Button>
          </>
        )}

        {modo === 'enviado' && (
          <>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#0d1b2e', mb: 1.5 }}>
              Verifique seu e-mail
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5, lineHeight: 1.6 }}>
              Se esse usuário existir no sistema, enviamos um e-mail com um link para você redefinir sua senha.
              O link é válido por algumas horas.
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setModo('login')}
              sx={{ borderColor: '#13375F', color: '#13375F', py: 1.2, fontWeight: 700 }}
            >
              Voltar ao login
            </Button>
          </>
        )}
      </CartaoAutenticacao>
    </Box>
  );
}